import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError, api } from '../api';
import type { Permission, Role, Tag } from '../api';
import { useAuth, useTenantSession } from '../auth';
import { Banner, Empty, Field, Modal, Spinner, TagEditor, TagList } from '../components/ui';

export function Roles() {
  const { token } = useTenantSession();
  const { can } = useAuth();
  const [rows, setRows] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Role | 'new' | null>(null);
  const [removing, setRemoving] = useState<Role | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [roleRes, permRes] = await Promise.all([
        api.listRoles(token, search),
        // Needed to render the permission picker; a member with role.read but
        // not permission.read still gets the role list, just without the picker.
        can('permission.read') ? api.listPermissions(token) : Promise.resolve({ permissions: [], total: 0 }),
      ]);
      setRows(roleRes.roles);
      setPermissions(permRes.permissions);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load roles');
    } finally {
      setLoading(false);
    }
  }, [token, search, can]);

  useEffect(() => {
    const t = setTimeout(() => void load(), search ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  async function onDelete() {
    if (!removing) return;
    try {
      await api.deleteRole(token, removing.id);
      setRemoving(null);
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete the role');
      setRemoving(null);
    }
  }

  if (!can('role.read')) return <Empty>You do not have permission to view roles.</Empty>;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Roles</h1>
          <p className="muted">Bundles of permissions you assign to users.</p>
        </div>
        {can('role.create') && (
          <button type="button" className="btn btn--primary" onClick={() => setEditing('new')}>
            New role
          </button>
        )}
      </div>

      {error && <Banner kind="error" onDismiss={() => setError(null)}>{error}</Banner>}

      <input
        className="input input--search"
        placeholder="Search roles…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Empty>No roles match.</Empty>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Permissions</th>
                <th>Tags</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((role) => (
                <tr key={role.id}>
                  <td>{role.name}</td>
                  <td className="muted">{role.description || '—'}</td>
                  <td>
                    {role.permissions.length === 0 ? (
                      <span className="muted">None</span>
                    ) : (
                      <span className="tag-list">
                        {role.permissions.map((p) => (
                          <span className="chip chip--code" key={p.id}>
                            {p.code}
                          </span>
                        ))}
                      </span>
                    )}
                  </td>
                  <td>
                    <TagList tags={role.tags} />
                  </td>
                  <td className="right row-actions">
                    {can('role.update') && (
                      <button type="button" className="btn btn--small" onClick={() => setEditing(role)}>
                        Edit
                      </button>
                    )}
                    {can('role.delete') && (
                      <button type="button" className="btn btn--small btn--danger" onClick={() => setRemoving(role)}>
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <RoleModal
          token={token}
          role={editing === 'new' ? null : editing}
          permissions={permissions}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}

      {removing && (
        <Modal
          title="Delete role"
          onClose={() => setRemoving(null)}
          onSubmit={(e) => {
            e.preventDefault();
            void onDelete();
          }}
          submitLabel="Delete"
          destructive
        >
          <Banner kind="warn">
            Delete <strong>{removing.name}</strong>? Users holding it will lose the permissions it granted.
          </Banner>
        </Modal>
      )}
    </div>
  );
}

function RoleModal({
  token,
  role,
  permissions,
  onClose,
  onSaved,
}: {
  token: string;
  role: Role | null;
  permissions: Permission[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [selected, setSelected] = useState<string[]>(role?.permissions.map((p) => p.id) ?? []);
  const [tags, setTags] = useState<Tag[]>(role?.tags ?? []);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const body = {
      name: name.trim(),
      description: description.trim(),
      permissionIds: selected,
      tags: tags.filter((t) => t.key.trim()),
    };
    try {
      if (role) await api.updateRole(token, role.id, body);
      else await api.createRole(token, body);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the role');
      setSubmitting(false);
    }
  }

  return (
    <Modal title={role ? 'Edit role' : 'New role'} onClose={onClose} onSubmit={onSubmit} submitting={submitting}>
      {error && <Banner kind="error">{error}</Banner>}
      <Field label="Name">
        <input className="input" required value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Description">
        <textarea className="input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <Field label={`Permissions (${selected.length} selected)`}>
        {permissions.length === 0 ? (
          <p className="muted small">No permissions available to assign.</p>
        ) : (
          <div className="checklist">
            {permissions.map((p) => (
              <label className="checklist__item" key={p.id}>
                <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} />
                <span>
                  <code>{p.code}</code>
                  <span className="muted small"> — {p.name}</span>
                </span>
              </label>
            ))}
          </div>
        )}
      </Field>
      <Field label="Tags">
        <TagEditor value={tags} onChange={setTags} />
      </Field>
    </Modal>
  );
}
